import React, { useState, useEffect } from 'react';
import { Box, FormControl, InputLabel, TextField, Select, MenuItem, Autocomplete, OutlinedInput, Chip, IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useQuery, useLazyQuery } from '@apollo/client';
import i18next from 'i18next';
import moment from 'moment';
import { REPORT_LIST_HOTEL_GROUPS_QUERY, REPORT_LIST_HOTELS_QUERY, REPORT_LIST_PUBLIC_COMPANIES_QUERY, REPORT_LIST_PUBLIC_CLIENTS_QUERY } from '../../gql';
import { EOfferStatus, EHLBookingStatus, OffersReportFiltersInput, ListHotelsForReportsQuery, ECountryCode, ListPublicCompaniesQuery, ReportListHotelsQuery, ReportListPublicCompaniesQuery, ReportListPublicClientsQuery, EContentEmailTemplate, EOfferSource, EOfferVersionStatus } from '__generated__/graphql';
import { getContractTypeOptions } from '../utils/contractHelpers';
import { ContractType } from '__generated__/graphql';
import { allCountriesSorted } from 'languages';
import { getSubdivisionsForCountry, getSubdivisionName } from 'semshared/utils/subdivisions';
import { THandleFilterChange, useReportFilters } from '../hooks/useReportFilters';
import { SimpleDatePicker } from 'components/form/FormInputDate';

interface ReportFiltersProps {
  onFilterChange: THandleFilterChange;
  onReset: () => void;
}

export default function ReportFilters({ onFilterChange }: ReportFiltersProps) {
  const filters = useReportFilters();
  console.log('ReportFilters filters', filters);

  const { data: hotelGroupsData, loading: hotelGroupsLoading } = useQuery(REPORT_LIST_HOTEL_GROUPS_QUERY);
  const hotelGroups = hotelGroupsData?.reportListHotelGroups || [];

  const hotelStates = React.useMemo(
    () => getSubdivisionsForCountry(filters.hotelCountry || ''),
    [filters.hotelCountry]
  );

  const [hotelSearchInput, setHotelSearchInput] = useState<string>('');
  const [selectedHotel, setSelectedHotel] = useState<ReportListHotelsQuery['reportListHotels'][number] | null>(null);
  const [runListHotels, { data: hotelsData, loading: hotelsLoading }] = useLazyQuery(REPORT_LIST_HOTELS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const allHotels = hotelsData?.reportListHotels || [];

  // Ensure selected hotel is in options list
  const hotelOptions = React.useMemo(() => {
    if (!selectedHotel) return allHotels;
    const isInList = allHotels.some(hotel => hotel.id === selectedHotel.id);
    return isInList ? allHotels : [selectedHotel, ...allHotels];
  }, [allHotels, selectedHotel]);

  // Load hotel by ID when hotelId is set in filters
  useEffect(() => {
    if (filters.hotelId && !selectedHotel) {
      runListHotels({
        variables: {
          id: filters.hotelId,
        },
      });
    }
  }, [filters.hotelId, selectedHotel, runListHotels]);

  // Sync selectedHotel with filters.hotelId when hotel is found in allHotels
  useEffect(() => {
    if (filters.hotelId && !selectedHotel) {
      const foundHotel = allHotels.find(hotel => hotel.id === filters.hotelId);
      if (foundHotel) {
        setSelectedHotel(foundHotel);
        setHotelSearchInput(foundHotel.name || '');
      }
    } else if (!filters.hotelId && selectedHotel) {
      setSelectedHotel(null);
      setHotelSearchInput('');
    }
  }, [filters.hotelId, allHotels, selectedHotel]);

  // Trigger hotel search when input has at least 3 characters
  useEffect(() => {
    const handler = setTimeout(() => {
      if (hotelSearchInput && hotelSearchInput.length >= 3) {
        runListHotels({
          variables: {
            country: filters.hotelCountry,
            state: filters.hotelState,
            groupIds: filters.hotelGroupIds,
            contractType: filters.hotelContractType !== 'NONE' ? filters.hotelContractType as ContractType : undefined,
            name: hotelSearchInput,
          },
        });
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [hotelSearchInput, filters.hotelCountry, filters.hotelState, filters.hotelGroupIds, filters.hotelContractType, runListHotels]);

  const [companySearchInput, setCompanySearchInput] = useState<string>('');
  const [selectedCompany, setSelectedCompany] = useState<ReportListPublicCompaniesQuery['reportListPublicCompanies'][number] | null>(null);
  const [runListCompanies, { data: companiesData, loading: companiesLoading }] = useLazyQuery(REPORT_LIST_PUBLIC_COMPANIES_QUERY, {
    fetchPolicy: 'network-only',
  });
  const allCompanies = companiesData?.reportListPublicCompanies || [];

  // Ensure selected company is in options list
  const companyOptions = React.useMemo(() => {
    if (!selectedCompany) return allCompanies;
    const isInList = allCompanies.some(company => company.id === selectedCompany.id);
    return isInList ? allCompanies : [selectedCompany, ...allCompanies];
  }, [allCompanies, selectedCompany]);

  // Sync selectedCompany with filters.clientCompanyId when company is found in allCompanies
  useEffect(() => {
    if (filters.clientCompanyId && !selectedCompany) {
      const foundCompany = allCompanies.find(company => company.id === filters.clientCompanyId);
      if (foundCompany) {
        setSelectedCompany(foundCompany);
        setCompanySearchInput(foundCompany.name || '');
      }
    } else if (!filters.clientCompanyId && selectedCompany) {
      setSelectedCompany(null);
      setCompanySearchInput('');
    }
  }, [filters.clientCompanyId, allCompanies, selectedCompany]);

  // Trigger company search when input has at least 3 characters
  useEffect(() => {
    const handler = setTimeout(() => {
      if (companySearchInput && companySearchInput.length >= 3) {
        runListCompanies({
          variables: {
            country: filters.clientCountry,
            isPremium: filters.clientIsPremium,
            name: companySearchInput,
          },
        });
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [companySearchInput, filters.clientCountry, filters.clientIsPremium, runListCompanies]);

  const [clientSearchInput, setClientSearchInput] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<ReportListPublicClientsQuery['reportListPublicClients'][number] | null>(null);
  const [runListClients, { data: clientsData, loading: clientsLoading }] = useLazyQuery(REPORT_LIST_PUBLIC_CLIENTS_QUERY, {
    fetchPolicy: 'network-only',
  });
  const allClients = clientsData?.reportListPublicClients || [];

  // Ensure selected client is in options list
  const clientOptions = React.useMemo(() => {
    if (!selectedClient) return allClients;
    const isInList = allClients.some(client => client.id === selectedClient.id);
    return isInList ? allClients : [selectedClient, ...allClients];
  }, [allClients, selectedClient]);

  // Load client by ID when clientId is set in filters
  useEffect(() => {
    if (filters.publicClientId && !selectedClient) {
      runListClients({
        variables: {
          id: filters.publicClientId,
        },
      });
    }
  }, [filters.publicClientId, selectedClient, runListClients]);

  // Sync selectedClient with filters.clientId when client is found in allClients
  useEffect(() => {
    if (filters.publicClientId && !selectedClient) {
      const foundClient = allClients.find(client => client.id === filters.publicClientId);
      if (foundClient) {
        setSelectedClient(foundClient);
        const clientName = `${foundClient.firstname || ''} ${foundClient.lastname || ''}`.trim();
        setClientSearchInput(clientName);
      }
    } else if (!filters.publicClientId && selectedClient) {
      setSelectedClient(null);
      setClientSearchInput('');
    }
  }, [filters.publicClientId, allClients, selectedClient]);

  // Trigger client search when input has at least 3 characters OR when company is selected
  useEffect(() => {
    const handler = setTimeout(() => {
      // Execute query if: input has at least 3 characters OR a company is selected
      if (filters.clientCompanyId || (clientSearchInput && clientSearchInput.length >= 3)) {
        runListClients({
          variables: {
            country: filters.clientCountry,
            isPremium: filters.clientIsPremium,
            publicCompanyId: filters.clientCompanyId,
            name: clientSearchInput && clientSearchInput.length >= 3 ? clientSearchInput : undefined,
          },
        });
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [clientSearchInput, filters.clientCountry, filters.clientIsPremium, filters.clientCompanyId, runListClients]);

  const [inquiryStatusType, setInquiryStatusType] = useState<'offer' | 'offerVersion' | 'booking' | undefined>('offer');

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 3, mb: 4 }}>
      {/* Erste Spalte: Datum */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ fontWeight: 'bold', mb: 1 }}>{i18next.t('filter-date')}</Box>
        <FormControl sx={{ minWidth: 180 }}>
          <SimpleDatePicker
            label={i18next.t('filter-from')}
            value={filters.requestDateFrom || null}
            onChange={(date) => onFilterChange('requestDateFrom', date)}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 180 }}>
          <SimpleDatePicker
            label={i18next.t('filter-to')}
            value={filters.requestDateTo || null}
            onChange={(date) => onFilterChange('requestDateTo', date)}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 180 }}>
          <SimpleDatePicker
            label={i18next.t('filter-seminar-from')}
            value={filters.eventDateFrom ? new Date(filters.eventDateFrom) : null}
            onChange={(date) => onFilterChange('eventDateFrom', date ? moment(date).format('YYYY-MM-DD') : null)}
          />
        </FormControl>
        <FormControl sx={{ minWidth: 180 }}>
          <SimpleDatePicker
            label={i18next.t('filter-seminar-to')}
            value={filters.eventDateTo ? new Date(filters.eventDateTo) : null}
            onChange={(date) => onFilterChange('eventDateTo', date ? moment(date).format('YYYY-MM-DD') : null)}
          />
        </FormControl>
      </Box>

      {/* Zweite Spalte: Status */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ fontWeight: 'bold', mb: 1 }}>{i18next.t('filter-status')}</Box>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-offer-source')}</InputLabel>
          <Select
            label={i18next.t('filter-offer-source')}
            value={filters.offerSource || ''}
            onChange={(e) => onFilterChange('offerSource', e.target.value as EOfferSource || undefined)}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-offer-source-all')}</MenuItem>
            {Object.values(EOfferSource).map(source => (
              <MenuItem key={source} value={source}>
                {i18next.t(`enums-EOfferSource-${source}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-inquiry-status-type')}</InputLabel>
          <Select
            label={i18next.t('filter-inquiry-status-type')}
            value={inquiryStatusType || ''}
            onChange={(e) => setInquiryStatusType(e.target.value === 'offer' ? 'offer' : e.target.value === 'offerVersion' ? 'offerVersion' : e.target.value === 'booking' ? 'booking' : undefined)}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {[
              { value: 'offer', label: i18next.t('filter-status-offer') },
              { value: 'offerVersion', label: i18next.t('filter-status-offer-version') },
              { value: 'booking', label: i18next.t('filter-status-booking') }
            ].map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-status')}</InputLabel>
          {inquiryStatusType === 'offer' && (
            <Select
              label={i18next.t('filter-offer-state')}
              value={filters.offerState || ''}
              onChange={(e) => onFilterChange('offerState', e.target.value || undefined)}
              disabled={!inquiryStatusType}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {Object.values(EOfferStatus).map(status => (
                <MenuItem key={status} value={status}>
                  {i18next.t(`enums-EOfferStatus-${status}`)}
                </MenuItem>
              ))}
            </Select>
          )}
          {inquiryStatusType === 'offerVersion' && (
            <Select
              label={i18next.t('filter-offer-version-state')}
              value={filters.offerVersionState || ''}
              onChange={(e) => onFilterChange('offerVersionState', e.target.value || undefined)}
              disabled={!inquiryStatusType}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {Object.values(EOfferVersionStatus).map(status => (
                <MenuItem key={status} value={status}>
                  {i18next.t(`enums-EOfferVersionStatus-${status}`)}
                </MenuItem>
              ))}
            </Select>
          )}
          {inquiryStatusType === 'booking' && (
            <Select
              label={i18next.t('filter-booking-state')}
              value={filters.bookingState}
              onChange={(e) => onFilterChange('bookingState', e.target.value || undefined)}
              disabled={!inquiryStatusType}
              displayEmpty
              notched
            >
              <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
              {Object.values(EHLBookingStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {i18next.t(`enums-EHLBookingStatus-${status}`)}
                </MenuItem>
              ))}
            </Select>
          )}
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-cancellation')}</InputLabel>
          <Select
            label={i18next.t('filter-cancellation')}
            value={filters.cancellation === undefined ? '' : filters.cancellation ? 'yes' : 'no'}
            onChange={(e) => onFilterChange('cancellation', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined)}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            <MenuItem value="yes">{i18next.t('filter-cancellation-yes')}</MenuItem>
            <MenuItem value="no">{i18next.t('filter-cancellation-no')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Dritte Spalte: Hotel */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ fontWeight: 'bold', mb: 1 }}>{i18next.t('filter-hotel')}</Box>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-agreement-type')}</InputLabel>
          <Select
            label={i18next.t('filter-agreement-type')}
            value={filters.hotelContractType || ''}
            onChange={(e) => onFilterChange('hotelContractType', e.target.value as string || undefined)}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            <MenuItem value="NONE">{i18next.t('filter-agreement-no-contract')}</MenuItem>
            {getContractTypeOptions().filter(o => ['SYMPOSION_HOTEL_CONTRACT', 'PARTNER_HOTEL_CONTRACT', 'FRIEND_HOTEL_CONTRACT', 'FRIEND_LOCATION_CONTRACT'].indexOf(o.value) !== -1).map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-country-hotel')}</InputLabel>
          <Select
            label={i18next.t('filter-country-hotel')}
            value={filters.hotelCountry || ''}
            onChange={(e) => onFilterChange('hotelCountry', e.target.value ? e.target.value as ECountryCode : undefined)}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {allCountriesSorted.map(country => (
              country && <MenuItem key={country.code} value={country.code}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-state')}</InputLabel>
          <Select
            label={i18next.t('filter-state')}
            value={filters.hotelState || ''}
            onChange={(e) => onFilterChange('hotelState', e.target.value ? e.target.value as string : undefined)}
            disabled={!filters.hotelCountry}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {hotelStates.map((state) => (
              <MenuItem key={state.code} value={state.code}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <FormControl sx={{ minWidth: 180, flex: 1 }}>
            <InputLabel shrink>{i18next.t('filter-hotel-group')}</InputLabel>
            <Select
              multiple
              label={i18next.t('filter-hotel-group')}
              value={filters.hotelGroupIds || []}
              onChange={(e) => onFilterChange('hotelGroupIds', e.target.value ? typeof e.target.value === 'string' ? e.target.value.split(',').map(Number) : e.target.value.map(Number) : undefined)}
              input={<OutlinedInput id="select-multiple-chip" label={i18next.t('filter-hotel-group')} />}
              disabled={hotelGroupsLoading}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <Box sx={{ color: 'text.secondary' }}>{i18next.t('filter-hotel-group-select')}</Box>;
                }
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={hotelGroups.find(group => group.id === value)?.name || ''} />
                    ))}
                  </Box>
                );
              }}
              notched
            >
              {hotelGroups.map((group) => (
                <MenuItem
                  key={group.id}
                  value={group.id}
                >
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {filters.hotelGroupIds && filters.hotelGroupIds.length > 0 && (
            <IconButton
              size="small"
              onClick={() => onFilterChange('hotelGroupIds', undefined)}
              sx={{
                mt: 1,
                padding: 0.25,
                minWidth: 'auto',
                width: 'auto',
                height: 'auto'
              }}
              title={i18next.t('filter-reset') || undefined}
            >
              <ClearIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>

        <FormControl sx={{ minWidth: 180 }}>
          <Autocomplete
            options={hotelOptions}
            getOptionLabel={(option) => {
              const parts = [option.name || ''];
              if (option.businessZip || option.businessCity) {
                const location = [option.businessZip, option.businessCity].filter(Boolean).join(' ');
                if (location) parts.push(location);
              }
              return parts.join(', ');
            }}
            value={selectedHotel}
            inputValue={hotelSearchInput}
            onInputChange={(_, newInputValue) => {
              setHotelSearchInput(newInputValue);
              // Clear hotelId and selected hotel if input is cleared
              if (!newInputValue) {
                if (filters.hotelId) {
                  onFilterChange('hotelId', undefined);
                }
                setSelectedHotel(null);
              }
            }}
            onChange={(_, newValue) => {
              setSelectedHotel(newValue);
              onFilterChange('hotelId', newValue ? newValue.id : undefined);
              // Update input to show selected hotel name
              if (newValue) {
                setHotelSearchInput(newValue.name || '');
              } else {
                setHotelSearchInput('');
              }
            }}
            loading={hotelsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18next.t('filter-hotel')}
                variant="outlined"
                placeholder={String(i18next.t('filter-hotel-search-placeholder'))}
                InputLabelProps={{ shrink: true }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={hotelSearchInput.length < 3 ? String(i18next.t('filter-enter-min-3')) : String(i18next.t('filter-hotels-not-found'))}
            clearText={String(i18next.t('delete'))}
          />
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-show-in-lister')}</InputLabel>
          <Select
            label={i18next.t('filter-show-in-lister')}
            value={filters.showInLister === undefined ? '' : filters.showInLister ? 'yes' : 'no'}
            onChange={(e) => onFilterChange('showInLister', e.target.value === 'yes' ? true : e.target.value === 'no' ? false : undefined)}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            <MenuItem value="yes">{i18next.t('filter-show-in-lister-yes')}</MenuItem>
            <MenuItem value="no">{i18next.t('filter-show-in-lister-no')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Vierte Spalte: Kunde */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ fontWeight: 'bold', mb: 1 }}>{i18next.t('filter-customer')}</Box>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-customer-type')}</InputLabel>
          <Select
            label={i18next.t('filter-customer-type')}
            value={filters.clientIsPremium === undefined ? '' : filters.clientIsPremium ? 'premium' : 'classic'}
            onChange={(e) => onFilterChange('clientIsPremium', e.target.value === 'premium' ? true : e.target.value === 'classic' ? false : undefined)}
            disabled={companiesLoading}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {[
              { value: 'classic', label: i18next.t('filter-customer-type-classic') },
              { value: 'premium', label: i18next.t('filter-customer-type-premium') }
            ].map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel shrink>{i18next.t('filter-customer-country')}</InputLabel>
          <Select
            label={i18next.t('filter-customer-country')}
            value={filters.clientCountry || ''}
            onChange={(e) => onFilterChange('clientCountry', e.target.value ? e.target.value as ECountryCode : undefined)}
            disabled={clientsLoading}
            displayEmpty
            notched
          >
            <MenuItem value="">{i18next.t('filter-status-all')}</MenuItem>
            {allCountriesSorted.map(country => (
              country && <MenuItem key={country.code} value={country.code}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <Autocomplete
            options={companyOptions}
            getOptionLabel={(option) => {
              const parts = [option.name || ''];
              if (option.zip || option.city) {
                const location = [option.zip, option.city].filter(Boolean).join(' ');
                if (location) parts.push(location);
              }
              return parts.join(', ');
            }}
            value={selectedCompany}
            inputValue={companySearchInput}
            onInputChange={(_, newInputValue) => {
              setCompanySearchInput(newInputValue);
              // Clear clientCompanyId and selected company if input is cleared
              if (!newInputValue) {
                if (filters.clientCompanyId) {
                  onFilterChange('clientCompanyId', undefined);
                }
                setSelectedCompany(null);
              }
            }}
            onChange={(_, newValue) => {
              setSelectedCompany(newValue);
              onFilterChange('clientCompanyId', newValue ? newValue.id : undefined);
              // Update input to show selected company name
              if (newValue) {
                setCompanySearchInput(newValue.name || '');
              } else {
                setCompanySearchInput('');
              }
            }}
            loading={companiesLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18next.t('filter-company')}
                variant="outlined"
                placeholder={String(i18next.t('filter-company-search-placeholder'))}
                InputLabelProps={{ shrink: true }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={companySearchInput.length < 3 ? String(i18next.t('filter-enter-min-3')) : String(i18next.t('filter-companies-not-found'))}
            clearText={String(i18next.t('delete'))}
          />
        </FormControl>

        <FormControl sx={{ minWidth: 180 }}>
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(option) => {
              const parts = [`${option.firstname || ''} ${option.lastname || ''}`.trim()];
              if (option.publicCompany?.name) {
                parts.push(option.publicCompany.name);
              }
              if (option.publicCompany?.zip || option.publicCompany?.city) {
                const location = [option.publicCompany.zip, option.publicCompany.city].filter(Boolean).join(' ');
                if (location) parts.push(location);
              }
              return parts.join(', ');
            }}
            value={selectedClient}
            inputValue={clientSearchInput}
            onInputChange={(_, newInputValue) => {
              setClientSearchInput(newInputValue);
              // Clear clientId and selected client if input is cleared
              if (!newInputValue) {
                if (filters.publicClientId) {
                  onFilterChange('publicClientId', undefined);
                }
                setSelectedClient(null);
              }
            }}
            onChange={(_, newValue) => {
              setSelectedClient(newValue);
              onFilterChange('publicClientId', newValue ? newValue.id : undefined);
              // Update input to show selected client name
              if (newValue) {
                const clientName = `${newValue.firstname || ''} ${newValue.lastname || ''}`.trim();
                setClientSearchInput(clientName);
              } else {
                setClientSearchInput('');
              }
            }}
            loading={clientsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18next.t('filter-contact-person')}
                variant="outlined"
                placeholder={String(i18next.t('filter-contact-search-placeholder'))}
                InputLabelProps={{ shrink: true }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={clientSearchInput.length < 3 ? String(i18next.t('filter-enter-min-3')) : String(i18next.t('filter-contacts-not-found'))}
            clearText={String(i18next.t('delete'))}
          />
        </FormControl>
      </Box>
    </Box>
  );
}